import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _Skeleton;
import { lazy, Suspense, useCallback, useState } from 'react';
import { Skeleton } from '@crehana/compass.ui/skeleton';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { FEATURE_FLAG_ENABLE_CHILDREN_ORGANIZATION_MANAGEMENT } from "../../../feature-flags";
import { useCorporateOrganizations } from "./centralized-company-selector/hooks/use-corporate-organizations";
import { useGetCentralizedSessionData } from "./hooks/use-get-centralized-session-data";
import RoleSelector from "./RoleSelector";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var CompaniesDropdown = /*#__PURE__*/lazy(function () {
  return import("./CompaniesDropdown");
});
var CentralizedCompanySelector = /*#__PURE__*/lazy(function () {
  return import("./centralized-company-selector").then(function (module) {
    return {
      "default": module.CentralizedCompanySelector
    };
  });
});
export function ProfileInnerSelectorInner(_ref) {
  var isLearningCompanySelectorEnabled = _ref.canRenderCompaniesDropdown,
    _ref$organizationsLis = _ref.organizationsList,
    organizationsList = _ref$organizationsLis === void 0 ? [] : _ref$organizationsLis,
    defaultSelectedOrganization = _ref.defaultSelectedOrganization,
    userRoles = _ref.userRoles,
    defaultSelectedRole = _ref.defaultSelectedRole,
    enableUserPermissions = _ref.enableUserPermissions,
    organizationSlug = _ref.organizationSlug,
    isSupplanting = _ref.isSupplanting,
    isEnabled = _ref.isEnabled,
    isEnabledLoading = _ref.isEnabledLoading;
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchTerm = _useState2[0],
    setSearchTerm = _useState2[1];
  var _useCorporateOrganiza = useCorporateOrganizations({
      slug: organizationSlug,
      search: searchTerm,
      skip: isEnabledLoading || !isEnabled
    }),
    isCorporation = _useCorporateOrganiza.isCorporation,
    isCurrentParentOrg = _useCorporateOrganiza.isCurrentParentOrg,
    isCorporationEmpty = _useCorporateOrganiza.isCorporationEmpty,
    currentOrganization = _useCorporateOrganiza.currentOrganization,
    organizations = _useCorporateOrganiza.organizations,
    fetchMore = _useCorporateOrganiza.fetchMore,
    refetch = _useCorporateOrganiza.refetch,
    loading = _useCorporateOrganiza.loading,
    fetchingMore = _useCorporateOrganiza.fetchingMore,
    hasMore = _useCorporateOrganiza.hasMore,
    count = _useCorporateOrganiza.count,
    error = _useCorporateOrganiza.error,
    parentOrganizationId = _useCorporateOrganiza.parentOrganizationId;

  // Show centralized selector for corporations OR their children organizations
  var isPartOfCorporation = isCorporation || Boolean(parentOrganizationId);
  var isCentralizedCompanySelectorEnabled = isEnabled && isPartOfCorporation && !error;

  /**
   * Retrocompatibility logic for role selector visibility:
   * - When newApiEnabled is true and there are no errors: Uses the new corporate organizations logic (isCurrentParentOrg && !error)
   * - When newApiEnabled is false or there are errors: Defaults to true to maintain legacy behavior
   * This ensures backward compatibility while the new API is being rolled out
   */
  var retroCompatOverrideIsCurrentParentOrg = isCentralizedCompanySelectorEnabled ? isCurrentParentOrg : true;
  var legacyShouldShowRoleSelector = (userRoles === null || userRoles === void 0 ? void 0 : userRoles.length) > 1 && defaultSelectedRole;

  /**
   * Determines whether the role selector should be displayed based on multiple conditions:
   *
   * - userRoles?.length > 1: Only show role selector when user has multiple roles to choose from
   * - defaultSelectedRole: Ensures there's a valid default role selected before showing the selector
   * - retroCompatOverrideIsCurrentParentOrg: Handles role selector visibility with retrocompatibility:
   *   * New API enabled: Only shows when current org is parent AND no errors (corporate admins cannot change role in child organizations)
   *   * Legacy API: Always shows (maintains existing behavior)
   *
   * This prevents showing an empty or unnecessary role selector when:
   * - User has only one role (no choice to make)
   * - No default role is available
   * - User is in a child organization context (new API only)
   */
  var shouldShowRoleSelector = legacyShouldShowRoleSelector && retroCompatOverrideIsCurrentParentOrg;
  var handleSearch = useCallback(function (search) {
    setSearchTerm(search);
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldShowRoleSelector && /*#__PURE__*/_jsx(RoleSelector
    // This key is really important to let the component re-render if it gets a different value as default
    , {
      options: userRoles,
      defaultSelected: defaultSelectedRole,
      organizationSlug: organizationSlug,
      enableUserPermissions: enableUserPermissions,
      isSupplanting: isSupplanting
    }, defaultSelectedRole.value), !isEnabledLoading && isLearningCompanySelectorEnabled && !isCentralizedCompanySelectorEnabled && /*#__PURE__*/_jsx(CompaniesDropdown, {
      options: organizationsList,
      defaultSelectedItem: defaultSelectedOrganization,
      isSupplanting: isSupplanting
    }), !isEnabledLoading &&
    // Check if this conditional can be safely removed
    isLearningCompanySelectorEnabled && isCentralizedCompanySelectorEnabled && !isCorporationEmpty && /*#__PURE__*/_jsx(CentralizedCompanySelector, {
      isSupplanting: isSupplanting,
      onSearch: handleSearch,
      currentOrganization: currentOrganization,
      organizations: organizations,
      fetchMore: fetchMore,
      refetch: refetch,
      loading: loading,
      fetchingMore: fetchingMore,
      hasMore: hasMore,
      count: count,
      error: error,
      parentOrganizationId: parentOrganizationId
    })]
  });
}
export var ProfileInnerSelectors = function ProfileInnerSelectors(props) {
  var _useGetCentralizedSes = useGetCentralizedSessionData(props.organizationSlug),
    isCentralizedCorporationSuperAdmin = _useGetCentralizedSes.isCentralizedSuperAdmin,
    isSessionDataLoading = _useGetCentralizedSes.loading,
    errorSessionData = _useGetCentralizedSes.error;
  var _useFeatureFlag = useFeatureFlag(FEATURE_FLAG_ENABLE_CHILDREN_ORGANIZATION_MANAGEMENT[0], FEATURE_FLAG_ENABLE_CHILDREN_ORGANIZATION_MANAGEMENT[1]),
    isCentralizedChildrenOrganizationManagementEnabled = _useFeatureFlag.flagValue,
    isFeatureFlagLoading = _useFeatureFlag.loading,
    errorFeatureFlag = _useFeatureFlag.error;
  var isEnabled = !isFeatureFlagLoading && !errorFeatureFlag && isCentralizedChildrenOrganizationManagementEnabled && !isSessionDataLoading && !errorSessionData && isCentralizedCorporationSuperAdmin;
  var isEnabledLoading = isFeatureFlagLoading || isSessionDataLoading;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: !isFeatureFlagLoading && /*#__PURE__*/_jsx(Suspense, {
      fallback: _Skeleton || (_Skeleton = /*#__PURE__*/_jsx(Skeleton, {
        className: "tw-w-full tw-my-1 tw-px-0.8 tw-h-4.8 tw-rounded-0.8"
      })),
      children: /*#__PURE__*/_jsx(ProfileInnerSelectorInner, _objectSpread(_objectSpread({}, props), {}, {
        isEnabled: isEnabled,
        isEnabledLoading: isEnabledLoading
      }))
    })
  });
};